import { flushSync } from 'react-dom';
import type { ViewName } from './roles.js';

export type NavDirection = 'forward' | 'back';

/**
 * 画面の深さ。部署の画面（ホーム）を手前、全体ボード・集計・名簿・大型モニタをその先とみなす。
 * 深い方へ行くときは右から押し出し（iOS の push）、浅い方へ戻るときは左へ戻す（pop）。
 */
const DEPTH: Record<ViewName, number> = {
  clerk: 0,
  rx: 0,
  nurse: 0,
  board: 1,
  stats: 1,
  admin: 1,
  monitor: 1,
};

export function directionOf(from: ViewName, to: ViewName): NavDirection | null {
  if (from === to) return null;
  return DEPTH[to] < DEPTH[from] ? 'back' : 'forward';
}

/** スマホ幅（確定CSSのスマホ用ブレークポイントと同じ）。 */
const PHONE_QUERY = '(max-width: 760px)';

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { finished: Promise<void> };
};

let running = 0;

/**
 * 画面の切り替えを iOS のような横スライドで見せる（スマホ幅のときだけ）。
 *
 * View Transitions API（iOS Safari 18 以降・Android Chrome）で前後の画面の見た目を切り替える。
 * 使えない端末・動きを減らす設定・PC 幅では、アニメーションなしで切り替える。
 * 向きは <html data-nav-dir> で CSS に渡す（app.css の「移植時の追加：画面遷移」）。
 */
export function runPageTransition(direction: NavDirection | null, update: () => void): void {
  const doc = document as ViewTransitionDocument;
  const animate =
    direction !== null &&
    typeof doc.startViewTransition === 'function' &&
    window.matchMedia(PHONE_QUERY).matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!animate) {
    update();
    return;
  }
  const root = document.documentElement;
  const token = ++running;
  root.dataset.navDir = direction;
  const transition = doc.startViewTransition(() => flushSync(update));
  const clear = () => {
    if (token === running) delete root.dataset.navDir;
  };
  transition.finished.then(clear, clear);
}
